/**
 * @file   src\pages\MyInventoryListing.js
 * @brief  This file is responsible for my inventory listing.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import WantAdMoreIcon from "../images/want-ad-more-icon.svg";
import noImage from "../images/no-image-placeholder.png";
import Dropdown from "react-bootstrap/Dropdown";
import { getMyInventoryList } from "../services/inventoryService";
import { fetchUserDetails, fetchUserStatistics } from "../actions/userprofile";
import { useLocation, useNavigate } from "react-router-dom";
import MarkAsSoldConfirmModal from "../components/Inventory/MarkAsSoldConfirmModal";
import {
  getInventoryById,
  deleteMyInventory,
  inventoryMarkAsSold,
} from "../services/inventoryService";
import BedIcon from "../images/bed-icon.svg";
import BathIcon from "../images/bath-icon.svg";
import SqftIcon from "../images/sqft-icon.svg";

import {
  getLocalStorage,
  removeDecimalZeros,
  removeLocalStorage,
  setLocalStorage,
  truncate,
} from "../helpers/common";

import DeleteModal from "../components/Common/DeleteModal";
import {
  INVENTORY_KEY,
  INVENTORY_LTR,
  INVENTORY_LTR_STR,
  INVENTORY_SELL,
  INVENTORY_SELL_LTR,
  INVENTORY_SELL_STR,
  INVENTORY_STR,
  USER_INFO_KEY,
} from "../constants/common";
import { toast } from "react-toastify";

import { checkInventoryType, checkValidImage } from "../helpers/inventory";
import { USER_TYPE_AGENT } from "../constants/common";
import { pageURLs } from "@/constants";
import _ from "lodash";
import PremiumPlanModal from "../components/PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../components/PlanUpgrade/BasicPlanModal";
import { useDispatch, useSelector } from "react-redux";
import SelectDropDown from "../components/Common/SelectDropDown";
import HelpFeedbackModal from "../components/Inventory/HelpFeedbackModal";
import { replaceComma } from "../helpers/common";
import { getPropertyIcon, getTooltipText } from "../helpers/getPropertyIcons";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { themeColors } from "@/themeColors";
import { PageWrapper } from "@/components/PageWrapper";

const MyInventoryListing = (props) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const state = location.state;
  const [status, setStatus] = useState(
    state && state?.showDraft ? "Draft" : "Published",
  );
  const [currPage, setCurrPage] = useState(1); // storing current page number
  const [prevPage, setPrevPage] = useState(0); // storing prev page number
  const [wasLastList, setWasLastList] = useState(false); // setting a flag to know the last list
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState(false);
  const [markAsSoldStatus, setMarkAsSoldStatus] = useState(false);
  const [markAsSoldModalShow, setMarkAsSoldModal] = useState(false);
  const handleMarkAsSoldModal = (status) => setMarkAsSoldModal(status);
  const [myInventoryList, setMyInventoryList] = useState([]); // to set the inventory data
  const [inventoryId, setInventoryId] = useState(0);
  const [showNoResult, setShowNoResult] = useState(false);
  const PAGE_LIMIT = 10;
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);
  const [showBasicPlanModal, setBasicPlanModal] = useState(false);
  const BasicPlanModalClose = () => setBasicPlanModal(false);
  const BasicPlanModalShow = () => setBasicPlanModal(true);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const userStatictics = useSelector(
    (state) => state?.userProfile?.userStatictics,
  );
  const userPrivileges = useSelector(
    (state) => state?.userProfile?.userPrivileges,
  );
  const [userCount, setUserCount] = useState({});
  const [allowedCount, setAllowedCount] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [reload, setReload] = useState(0);
  const [options, setOptions] = useState([
    { value: "createdon", label: "Recently " + status },
    { value: "price", label: "Price" },
  ]);
  const [sortParam, setSortParam] = useState(options[0]);
  const [isSortParamChanged, setIsSortParamChanged] = useState(false);
  const [showGitchaHelpFeedbackModal, setShowGitchaHelpFeedbackModal] =
    useState(false);
  const GitchaHelpFeedbackModalShow = () =>
    setShowGitchaHelpFeedbackModal(true);
  const GitchaHelpFeedbackModalClose = () =>
    setShowGitchaHelpFeedbackModal(false);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    if (userPrivileges !== null && userPrivileges.length > 0) {
      setAllowedCount(userPrivileges[0]);
    } else {
      setAllowedCount({});
    }
  }, [userPrivileges]);

  useEffect(() => {
    if (userStatictics !== null && userStatictics.length > 0) {
      setUserCount(userStatictics[0]);
    } else {
      setUserCount({});
    }
  }, [userStatictics]);

  // to list my inventory

  useEffect(() => {
    if (_.get(sessionData, "userInfo.userId", "")) {
      dispatch(fetchUserDetails());
    }
  }, [reload]);

  useEffect(() => {
    const payload = {
      UserPlanId: userDetails?.User_Plan_Id,
    };

    dispatch(fetchUserStatistics(payload));
  }, [userDetails]);

  useEffect(() => {
    if (
      deleteStatus == true ||
      markAsSoldStatus == true ||
      isSortParamChanged
    ) {
      setCurrPage(1);
      setPrevPage(0);
      setMyInventoryList([]);
      setWasLastList(false);
      setDeleteStatus(false);
      setMarkAsSoldStatus(false);
      setIsSortParamChanged(false);
    }
    if (!wasLastList && prevPage !== currPage) {
      const data = {
        status: status,
        sortBy: sortParam?.value,
        page: currPage,
        limit: PAGE_LIMIT,
      };
      fetchMyInventory(data);
    }
  }, [
    currPage,
    wasLastList,
    status,
    sortParam?.value,
    deleteStatus == true,
    markAsSoldStatus == true,
    isSortParamChanged == true,
  ]);

  useEffect(() => {
    setOptions([
      { value: "createdon", label: "Recently " + status },
      { value: "price", label: "Price" },
    ]);
    setSortParam({ value: "createdon", label: "Recently " + status });
  }, [status]);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  const fetchMyInventory = async (query) => {
    await getMyInventoryList(query)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult.length > 0) {
          setMyInventoryList([...myInventoryList, ...responseResult]);
        } else if (responseResult && responseResult.length === 0) {
          setShowNoResult(false);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  // to list my inventory
  useEffect(() => {
    if (myInventoryList?.length > 0) {
      setPrevPage(currPage);
      setWasLastList(false);
    } else {
      setWasLastList(true);
      setShowNoResult(false);
    }
  }, [myInventoryList]);

  /**
   * To handle scroll for pagination
   */
  const handleScroll = () => {
    let userScrollHeight = window.innerHeight + window.scrollY;
    let windowBottomHeight = document.documentElement.offsetHeight;
    if (userScrollHeight >= windowBottomHeight) {
      setCurrPage((prevState) => prevState + 1);
    }
  };

  const handleSortParam = (selectedParam) => {
    setIsSortParamChanged(true);
    setSortParam(selectedParam);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll); // attaching scroll event listener
    if (currPage == 1) {
    }
    removeLocalStorage(INVENTORY_KEY);
  }, []);

  /**
   * inventory  delete button click action
   */
  const onInventoryDelete = (inventryId) => {
    setInventoryId(inventryId);
    setDeleteModalShow(true);
  };

  /**
   * inventory  mark as sold button click action
   */
  const onMarkAsSold = (inventryId) => {
    setInventoryId(inventryId);
    handleMarkAsSoldModal(true);
  };

  /**
   * To handle the inventory delete
   */
  const handleDeleteInventory = async () => {
    if (inventoryId) {
      setShowNoResult(true);
      await deleteMyInventory(inventoryId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);

          if (response.errorCode === 0 && responseResult) {
            toast.success("Your property has been deleted.");
            setDeleteModalShow(false);
            setDeleteStatus(!deleteStatus);
            updateReloadParam();
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
   * To handle the inventory mark as sold
   */
  const handleInventoryMarkAsSold = async (
    inventoryId,
    didGitchHelped,
    salesPrice,
  ) => {
    const newPrice = salesPrice?.replace("$", "");
    if (inventoryId) {
      setShowNoResult(true);
      const payload = {
        inventoryId: inventoryId,
        didGitchHelped: didGitchHelped === true ? 1 : 0,
        salesPrice: newPrice ? replaceComma(newPrice) : null,
      };
      await inventoryMarkAsSold(payload)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            toast.success("Your inventory has been successfully mark as sold.");
            GitchaHelpFeedbackModalClose();
            setMarkAsSoldStatus(!markAsSoldStatus);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  const handleInventortItemClick = (inventoryId) => {
    navigate(`${pageURLs.inventoryDetails}/${inventoryId}`, {
      state: { isFromListingPage: true },
    });
  };

  const renderTooltip = (Sub_PropertyTypeId) => (
    <Tooltip
      id="button-tooltip"
      placement="bottom-start"
      className="custom-tooltip"
      {...props}
    >
      {getTooltipText(Sub_PropertyTypeId)}
    </Tooltip>
  );

  /**
   * To list published inventory
   * @param props
   * @returns HTML
   */

  function Inventory(props) {
    const inventory = props.inventory;

    // const invType = checkInventoryType(_.get(inventory, "proprtyPurpose", ""));
    const invType = INVENTORY_SELL;

    const bedRoom = removeDecimalZeros(_.get(inventory, "Bedrooms", ""));
    const bathRoom = removeDecimalZeros(_.get(inventory, "Bathrooms", ""));
    const sqFt = removeDecimalZeros(_.get(inventory, "Sqft", ""));
    const acreage = removeDecimalZeros(_.get(inventory, "TotalAcre", ""));
    let lotSizeUnit = _.get(inventory, "Acre_Sqft", "");
    if (lotSizeUnit && lotSizeUnit !== 0) {
      lotSizeUnit = lotSizeUnit === 1 ? " Acres" : " Sqft";
    }
    const isDraftItem = _.get(inventory, "IsSavedDraft", "");
    const isSold = _.get(inventory, "IsSold", "");
    const inventoryId = _.get(inventory, "Inv_Id", "");
    const showMarkAsSoldBtn = !isDraftItem && !isSold;
    const photos = _.get(inventory, "photos", []);
    const mapImg = _.get(inventory, "mapImg", null);
    const fallbackPhoto = mapImg || noImage;
    const firstPhoto = checkValidImage({ photos: photos, index: 0 });
    const secondPhoto = checkValidImage({ photos: photos, index: 1 });
    const thirdPhoto = checkValidImage({ photos: photos, index: 2 });
    const Sub_PropertyTypeId = _.get(inventory, "Sub_PropertyTypeId", "");
    let titleText = "";
    switch (invType) {
      case INVENTORY_SELL:
        titleText = " selling";
        break;
      case INVENTORY_LTR:
        titleText = " renting";
        break;
      case INVENTORY_STR:
        titleText = " renting";
        break;
      case INVENTORY_LTR_STR:
        titleText = " renting";
        break;
      case INVENTORY_SELL_LTR:
        titleText = " selling, renting";
        break;
      case INVENTORY_SELL_STR:
        titleText = " selling, renting";
        break;
      default:
        titleText = " selling, renting";
        break;
    }

    return (
      <div className="col-sm-6 col-lg-4 mb-md-0">
        <a
          href={void 0}
          role={!isDraftItem ? "button" : ""}
          className="txt-no-underline"
          onClick={() =>
            !isDraftItem
              ? handleInventortItemClick(inventoryId)
              : onEdit(inventoryId, isDraftItem)
          }
        >
          <div className="my-wantad-listing">
            <div className="inventory-banner-small position-relative">
              <div className="modal-property-tile tile-loation">
                <OverlayTrigger
                  placement="bottom-start"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip(Sub_PropertyTypeId)}
                >
                  <div className="property-tile w-auto">
                    <img
                      src={getPropertyIcon(Sub_PropertyTypeId)}
                      alt={getPropertyIcon(Sub_PropertyTypeId)}
                      className="property-icon-img"
                    />
                  </div>
                </OverlayTrigger>
              </div>
              {photos.length === 0 && (
                <div className="banner-row">
                  <div className="banner-column-full w-100 h-166">
                    <div
                      className={`full-col-img-wrap h-100 ${mapImg ? "" : "placeholder-wrap-bg"} brdr-8`}
                    >
                      <img
                        className={`brdr-8 ${mapImg ? "col-img-style" : "placeholder-img-style"}`}
                        src={fallbackPhoto}
                      />
                    </div>
                  </div>
                </div>
              )}
              {photos.length === 1 && (
                <div className="banner-row">
                  <div className="banner-column-full w-100 h-166">
                    <div className="full-col-img-wrap h-100">
                      <img className="brdr-8 col-img-style" src={firstPhoto} />
                    </div>
                  </div>
                </div>
              )}
              {photos.length === 2 && (
                <div className="banner-row">
                  <div className="inventory-banner-small banner-column-half-small h-166">
                    <div className="half-col-img-wrap h-100">
                      <img
                        className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
                        src={firstPhoto}
                      />
                    </div>
                  </div>

                  <div className="inventory-banner-small banner-column-half-small h-166">
                    <div className="half-col-img-wrap-right h-100">
                      <img
                        className="brdr-top-right-8 brdr-btm-right-8 no-brdr-t-r-0 brdr-b-r-8 brdr-b-l-8 col-img-style "
                        src={secondPhoto}
                      />
                    </div>
                  </div>
                </div>
              )}
              {photos.length >= 3 && (
                <div className="banner-row">
                  <div className="banner-column-half h-166">
                    <div className="half-col-img-wrap h-100">
                      <img
                        className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brder-b-l-0 col-img-style"
                        src={firstPhoto}
                      />
                    </div>
                  </div>

                  <div className="banner-column-quarter h-166">
                    <div className="quarter-col-img-wrap-top h-50-percent">
                      <img
                        className="brdr-top-right-8 no-brdr-t-r-0 col-img-style"
                        src={secondPhoto}
                      />
                    </div>
                    <div className="quarter-col-img-wrap-btm h-50-percent">
                      <img
                        className="brdr-btm-right-8 brdr-b-l-8 col-img-style"
                        src={thirdPhoto}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="wantad-listing-details">
              <div className="wantad-listing-details-top d-flex justify-content-between">
                <div className="long-txt-fix">
                  <div
                    className={
                      isSold
                        ? "want-ad-tag mb-2 orange-tag-bg"
                        : "want-ad-tag mb-2 grey-tag-bg"
                    }
                  >
                    <span className="tag-icon grey-color-icon">
                      {isSold ? "CLOSED" : "Open To " + titleText}
                    </span>
                  </div>
                  <div
                    className={
                      _.get(inventory, "Intro_Count", "0") > 0
                        ? "want-ad-tag mb-2 green-tag-bg"
                        : "want-ad-tag mb-2 grey-tag-bg"
                    }
                  >
                    <span className="tag-icon grey-color-icon text-capitalize">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.30278 1.75224C8.00974 1.76581 7.7178 1.84107 7.44544 1.97726C7.43752 1.9812 7.42974 1.98538 7.42208 1.98979L6.59493 2.46773C6.5862 2.46728 6.57746 2.46709 6.56873 2.46716H1.02706C0.969085 2.46635 0.911527 2.47706 0.857728 2.49867C0.803929 2.52029 0.754964 2.55239 0.713677 2.59309C0.67239 2.6338 0.639606 2.68231 0.617229 2.7358C0.594852 2.78928 0.583328 2.84669 0.583328 2.90466C0.583328 2.96264 0.594852 3.02005 0.617229 3.07353C0.639606 3.12702 0.67239 3.17553 0.713677 3.21624C0.754964 3.25694 0.803929 3.28904 0.857728 3.31066C0.911527 3.33227 0.969085 3.34298 1.02706 3.34217H4.7498L4.55953 3.42135C4.55008 3.4252 4.54077 3.42938 4.53162 3.43388C4.04676 3.67631 3.82308 4.19274 3.87138 4.65751C3.91968 5.12229 4.25241 5.58564 4.78569 5.71879C4.99356 5.77059 5.22447 5.80082 5.47612 5.80082C6.75175 5.80082 8.00174 5.40334 8.3717 5.28072C8.61498 5.55233 9.81473 6.89309 10.2157 7.30359C10.5061 7.60071 10.5355 7.89079 10.4607 8.09713C10.3858 8.30347 10.216 8.46286 9.84599 8.46399C9.7455 8.46429 9.64818 8.49916 9.57038 8.56275C9.49257 8.62634 9.43903 8.71478 9.41874 8.81319C9.30966 9.34076 9.11715 9.41564 8.92086 9.46716C8.72456 9.51869 8.5352 9.474 8.5352 9.474C8.4714 9.45658 8.40448 9.45385 8.33947 9.46602C8.27446 9.47818 8.21306 9.50493 8.15987 9.54424C8.10669 9.58355 8.06311 9.63441 8.03241 9.69299C8.00171 9.75157 7.98469 9.81634 7.98263 9.88245C7.98263 9.88245 7.9681 10.1083 7.85673 10.3017C7.74536 10.4951 7.62938 10.6581 7.1099 10.6014C6.99965 10.5892 6.88891 10.6194 6.80008 10.6858C6.71125 10.7523 6.65095 10.8499 6.63139 10.9591C6.57185 11.2921 6.39078 11.4366 6.11926 11.4866C5.84775 11.5366 5.47903 11.4477 5.15654 11.1289C4.74362 10.7208 3.23802 9.18534 2.5566 8.48963C2.25489 8.18151 1.8417 8.00883 1.41158 8.00883H1.02706C0.969085 8.00801 0.911527 8.01872 0.857728 8.04034C0.803929 8.06196 0.754964 8.09405 0.713677 8.13476C0.67239 8.17547 0.639606 8.22397 0.617229 8.27746C0.594852 8.33095 0.583328 8.38835 0.583328 8.44633C0.583328 8.50431 0.594852 8.56171 0.617229 8.6152C0.639606 8.66869 0.67239 8.71719 0.713677 8.7579C0.754964 8.79861 0.803929 8.8307 0.857728 8.85232C0.911527 8.87394 0.969085 8.88465 1.02706 8.88383H1.41158C1.60913 8.88383 1.79414 8.96155 1.93168 9.10201C2.61351 9.79813 4.10489 11.3196 4.5413 11.7509C5.04452 12.2485 5.6934 12.455 6.27763 12.3474C6.75135 12.2601 7.16624 11.9298 7.37366 11.4587C7.87258 11.4171 8.25593 11.1879 8.48735 10.9147C8.49323 10.915 8.49912 10.9152 8.50501 10.9152C8.50501 10.9152 9.82157 10.9382 10.5541 10.9152C11.0637 10.8994 11.5774 10.7265 12.0306 10.3291C12.2351 10.1499 12.4811 10.0499 12.7313 10.0499H12.9854C13.0434 10.0507 13.1009 10.04 13.1547 10.0184C13.2085 9.9968 13.2575 9.96471 13.2988 9.924C13.3401 9.88329 13.3728 9.83478 13.3952 9.7813C13.4176 9.72781 13.4291 9.67041 13.4291 9.61243C13.4291 9.55445 13.4176 9.49705 13.3952 9.44356C13.3728 9.39007 13.3401 9.34156 13.2988 9.30086C13.2575 9.26015 13.2085 9.22806 13.1547 9.20644C13.1009 9.18482 13.0434 9.17411 12.9854 9.17493H12.7313C12.2489 9.17493 11.8009 9.36726 11.4541 9.6711C11.1357 9.95035 10.8828 10.0298 10.5273 10.0408H10.5267C10.2879 10.0483 9.95518 10.0468 9.64205 10.0465C9.86523 9.86253 10.0654 9.59509 10.1889 9.23759C10.6944 9.12203 11.1228 8.83819 11.2832 8.39563C11.4813 7.8494 11.3272 7.18916 10.8412 6.69177C10.4729 6.31472 8.83769 4.49288 8.83769 4.49288C8.78032 4.42887 8.70538 4.38317 8.6222 4.36149C8.53903 4.33981 8.45131 4.34311 8.37 4.37097C8.37 4.37097 6.70378 4.92582 5.47612 4.92582C5.29434 4.92582 5.13622 4.90454 4.9976 4.87C4.823 4.8264 4.75704 4.71335 4.74182 4.56694C4.72669 4.42136 4.7746 4.29283 4.92127 4.2183L6.29073 3.64978C6.30836 3.64237 6.32549 3.6338 6.342 3.62415L7.83907 2.75883C8.16986 2.59417 8.52132 2.58177 8.83997 2.70927L10.8116 3.49768C11.0356 3.58769 11.2749 3.63319 11.5157 3.63326L12.9854 3.63383C13.0434 3.63465 13.1009 3.62394 13.1547 3.60232C13.2085 3.5807 13.2575 3.54861 13.2988 3.5079C13.3401 3.46719 13.3728 3.41869 13.3952 3.3652C13.4176 3.31171 13.4291 3.25431 13.4291 3.19633C13.4291 3.13835 13.4176 3.08095 13.3952 3.02746C13.3728 2.97398 13.3401 2.92547 13.2988 2.88476C13.2575 2.84405 13.2085 2.81196 13.1547 2.79034C13.1009 2.76872 13.0434 2.75801 12.9854 2.75883L11.5162 2.75826C11.3862 2.75826 11.2579 2.73409 11.138 2.68591L11.1368 2.68535L9.16524 1.89693C8.88976 1.7867 8.59581 1.73867 8.30278 1.75224Z"
                          fill="#27AE60"
                        />
                      </svg>
                      {_.get(inventory, "Intro_Count", "0")}
                    </span>
                  </div>

                  <h2 className="long-txt-fix">
                    {truncate(_.get(inventory, "Location", ""), 24)}
                  </h2>
                </div>

                <div
                  className="side-nav-wrap"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Dropdown>
                    <Dropdown.Toggle id="dropdown-basic" variant="" align="end">
                      <img src={WantAdMoreIcon} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="dpdown-menu-style" align={"end"}>
                      {showMarkAsSoldBtn && (
                        <Dropdown.Item
                          onClick={() => onMarkAsSold(inventoryId)}
                        >
                          Mark as sold
                        </Dropdown.Item>
                      )}
                      {!isSold && (
                        <Dropdown.Item
                          onClick={() => onEdit(inventoryId, isDraftItem)}
                        >
                          Edit
                        </Dropdown.Item>
                      )}

                      <Dropdown.Item
                        onClick={() => onInventoryDelete(inventoryId)}
                      >
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              <ul className="wantad-btm-list mt-2">
                {bedRoom && (
                  <li>
                    <div className="list-icns">
                      <img src={BedIcon} />
                    </div>
                    <span className="list-txt">{bedRoom}</span>
                  </li>
                )}

                {bathRoom && (
                  <li>
                    <div className="list-icns">
                      <img src={BathIcon} />
                    </div>
                    <span className="list-txt">{bathRoom}</span>
                  </li>
                )}
                {sqFt && (
                  <li>
                    <div className="list-icns">
                      <img src={SqftIcon} />
                    </div>
                    <span className="list-txt">{sqFt} sqft</span>
                  </li>
                )}

                {acreage && (
                  <li>
                    <div className="list-icns">
                      <img src={SqftIcon} />
                    </div>
                    <span className="list-txt">{acreage + lotSizeUnit} </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </a>
      </div>
    );
  }

  /**
   * On edit button click
   * @param {*} inventoryId
   */
  const onEdit = (inventoryId, isDraftItem) => {
    if (isDraftItem && userCount?.Inventory >= allowedCount?.Inventory) {
      if (_.get(sessionData, "userInfo.userType", "") === USER_TYPE_AGENT) {
        PremiumPlanModalShow();
      } else {
        BasicPlanModalShow();
      }
    } else {
      getInventoryDetails(inventoryId);
    }
  };

  /**
   * get inventory details for edit
   * @param {*} inventoryId
   */
  const getInventoryDetails = async (inventoryId) => {
    if (inventoryId) {
      await getInventoryById(inventoryId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (responseResult.isAgentCreated === 1) {
              navigate(pageURLs.agentCreateInventory, {
                state: {
                  data: responseResult,
                  inventoryId: responseResult.inventoryId,
                },
              });
            } else {
              navigate(pageURLs.memberEditInventory, {
                state: { data: responseResult },
              });
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      navigate(pageURLs.agentCreatewantad);
    }
  };

  const handleTabChange = (tab) => {
    if (tab !== status) {
      setMyInventoryList([]);
      setCurrPage(1);
      setStatus(tab);
      setPrevPage(0);
      setWasLastList(false);
    }
  };

  const handleAddProperty = () => {
    if (userCount?.Inventory >= allowedCount?.Inventory) {
      if (_.get(sessionData, "userInfo.userType", "") === USER_TYPE_AGENT) {
        PremiumPlanModalShow();
      } else {
        BasicPlanModalShow();
      }
    } else {
      if (_.get(sessionData, "userInfo.userType", "") === USER_TYPE_AGENT) {
        navigate(pageURLs.agentCreateInventory);
      } else {
        navigate(pageURLs.memberCreateInventory);
      }
    }
  };

  return (
    <>
      <PageWrapper>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-3">
              <div className="col-md-12">
                <div className="my-want-ad-wrap">
                  <div className="top-sec mb-24 md-mt-24">
                    <div className="row m-3">
                      <div className="col">
                        <h2>My Inventory</h2>
                      </div>
                      <div
                        col="12"
                        className="d-flex d-sm-block flex-column col-sm-auto"
                      >
                        <div className="d-flex select-wrap">
                          <Form.Label className="">Sort By:</Form.Label>

                          <SelectDropDown
                            id="multi3"
                            name="colors3"
                            options={options}
                            className="basic-single my-inventory-select mb-0 ms-3"
                            classNamePrefix="select"
                            value={sortParam}
                            onChange={handleSortParam}
                            styles={{
                              dropdownIndicator: (provided, state) => ({
                                ...provided,
                                transform:
                                  state.selectProps.menuIsOpen &&
                                  "rotate(180deg)",
                              }),
                            }}
                            errorClass="error-msg error-msg-height"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="tab-wrap tab-wrap-fix">
                    <Tabs
                      id="controlled-tab-example"
                      className="mb-3"
                      unmountOnExit
                      activeKey={status}
                      onSelect={(k) => {
                        handleTabChange(k);
                      }}
                    >
                      <Tab eventKey="Published" title="Stored">
                        <div className="row g-md-5 responsive-row">
                          {myInventoryList !== null &&
                            myInventoryList?.length > 0 ? (
                            myInventoryList?.map((inv, i) => {
                              return (
                                <Inventory key={i} inventory={inv} ival={i} />
                              );
                            })
                          ) : (
                            <></>
                          )}

                          <div className="col-sm-6 col-lg-4 mb-0 mb-sm-0 mb-md-0">
                            <button
                              className="btn create-wantad-btn h-306"
                              onClick={handleAddProperty}
                            >
                              <span>
                                <svg
                                  width="15"
                                  height="14"
                                  viewBox="0 0 15 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M1.91669 7C1.91669 6.75838 2.11256 6.5625 2.35419 6.5625H11.9792C12.2208 6.5625 12.4167 6.75838 12.4167 7C12.4167 7.24162 12.2208 7.4375 11.9792 7.4375H2.35419C2.11256 7.4375 1.91669 7.24162 1.91669 7Z"
                                    fill={themeColors.neutral900}
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M7.16669 1.75C7.40831 1.75 7.60419 1.94588 7.60419 2.1875V11.8125C7.60419 12.0541 7.40831 12.25 7.16669 12.25C6.92506 12.25 6.72919 12.0541 6.72919 11.8125V2.1875C6.72919 1.94588 6.92506 1.75 7.16669 1.75Z"
                                    fill={themeColors.neutral900}
                                  />
                                </svg>
                              </span>{" "}
                              Add Property
                            </button>
                          </div>
                        </div>
                      </Tab>
                      <Tab eventKey="Closed" title="Closed">
                        <div className="row g-md-5 responsive-row">
                          {showNoResult ||
                            (myInventoryList !== null &&
                              myInventoryList?.length > 0)
                            ? myInventoryList?.map((inv, i) => {
                              return (
                                <Inventory key={i} inventory={inv} ival={i} />
                              );
                            })
                            : !showNoResult && (
                              <div className="no-result-wrap mw-435">
                                <h3>No results found</h3>
                              </div>
                            )}
                        </div>
                      </Tab>

                      <Tab eventKey="Draft" title="Drafts">
                        <div className="row g-md-5 responsive-row">
                          {showNoResult ||
                            (myInventoryList !== null &&
                              myInventoryList?.length > 0)
                            ? myInventoryList?.map((inv, i) => {
                              return (
                                <Inventory key={i} inventory={inv} ival={i} />
                              );
                            })
                            : !showNoResult && (
                              <div className="no-result-wrap mw-435">
                                <h3>No results found</h3>
                              </div>
                            )}
                        </div>
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>

      {/*  Delete confirmation Modal */}
      <DeleteModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        title={"Are you sure you want to permanently delete your property?"}
        message={
          "Deleting this property from your inventory will remove any pending and accepted introductions sent by you."
        }
        buttonText={" Delete Property"}
        handleConfirm={() => handleDeleteInventory()}
      />
      <PremiumPlanModal
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={PremiumPlanModalShow}
      />
      <BasicPlanModal
        userDetails={userDetails}
        show={showBasicPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={BasicPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={BasicPlanModalShow}
      />
      <MarkAsSoldConfirmModal
        show={markAsSoldModalShow}
        onHide={handleMarkAsSoldModal}
        handleInventoryMarkAsSold={() => {
          GitchaHelpFeedbackModalShow();
          handleMarkAsSoldModal(false);
        }}
        inventoryId={inventoryId}
      />
      <HelpFeedbackModal
        show={showGitchaHelpFeedbackModal}
        onHide={GitchaHelpFeedbackModalClose}
        handleInventoryMarkAsSold={handleInventoryMarkAsSold}
        inventoryId={inventoryId}
      />
    </>
  );
};

export default MyInventoryListing;